<template>
  <div>
    <!-- Dialog principal: histórico — oculto no modo openDirectAdd -->
    <v-dialog
      v-if="!openDirectAdd"
      v-model="internalValue"
      transition="dialog-bottom-transition"
      max-width="1200px"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header d-flex justify-space-between align-center">
          <div class="d-flex align-center" style="position: relative; z-index: 1;">
            <v-icon color="white" class="mr-2">mdi-human</v-icon>
            <span class="ds-dialog-title">
              Composição Corporal — Histórico
            </span>
          </div>
          <div class="d-flex align-center" style="position: relative; z-index: 1;">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  color="white"
                  class="mr-1 close-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="openAddForm"
                >
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <span>Adicionar medição</span>
            </v-tooltip>
            <v-btn icon color="white" class="close-btn" @click="closeDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <div v-if="loading" class="text-center py-10">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <section v-else-if="records.length" class="ds-form-section ds-form-section--flush">
            <v-simple-table class="history-table">
              <thead>
                <tr>
                  <th class="th-cell">Data</th>
                  <th class="th-cell text-right">Peso</th>
                  <th class="th-cell text-right">Altura</th>
                  <th class="th-cell text-right">IMC</th>
                  <th class="th-cell text-right">Cintura</th>
                  <th class="th-cell text-right">Quadril</th>
                  <th class="th-cell text-right">% Gord.</th>
                  <th class="th-cell text-right">% Magra</th>
                  <th class="th-cell text-right">Gord. kg</th>
                  <th class="th-cell text-right">Magra kg</th>
                  <th class="th-cell text-right">C/E</th>
                  <th class="th-cell text-right">C/Q</th>
                  <th class="th-cell text-center" style="width: 110px">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in records" :key="item.id" class="history-row">
                  <td class="text-no-wrap pa-3 text-caption">{{ formatDate(item.created_at) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.weight_kg) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.height_cm) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.bmi) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.waist_cm) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.hip_cm) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.fat_percent) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.lean_mass_percent) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.fat_kg) }}</td>
                  <td class="pa-3 text-right">{{ fmt(item.lean_mass_kg) }}</td>
                  <td
                    class="pa-3 text-right font-weight-bold"
                    :style="{ color: colorWaistHeight(item.waist_height_ratio) }"
                  >
                    {{ fmtRatio(item.waist_height_ratio) }}
                  </td>
                  <td
                    class="pa-3 text-right font-weight-bold"
                    :style="{ color: colorWaistHip(item.waist_hip_ratio) }"
                  >
                    {{ fmtRatio(item.waist_hip_ratio) }}
                  </td>
                  <td class="pa-3 text-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          @click="openEditForm(item)"
                        >
                          <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="error"
                          v-bind="attrs"
                          v-on="on"
                          @click="confirmDelete(item)"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section v-else class="ds-form-section text-center py-8 px-4">
            <v-icon size="56" color="grey lighten-1">mdi-scale-bathroom</v-icon>
            <p class="body-1 grey--text mt-3 mb-0">
              Nenhuma medição registrada.
            </p>
            <v-btn color="primary" class="mt-4" depressed @click="openAddForm">
              <v-icon small left>mdi-plus</v-icon>
              Adicionar primeira medição
            </v-btn>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de formulário (add/edit) -->
    <v-dialog v-model="formDialogVisible" max-width="720px" persistent scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header d-flex justify-space-between align-center">
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">
              {{ isEditMode ? "mdi-pencil" : "mdi-plus" }}
            </v-icon>
            <span class="ds-dialog-title">
              {{ isEditMode ? "Editar medição" : "Nova medição" }}
            </span>
          </div>
          <v-btn class="close-btn" icon color="white" @click="closeForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- Sem nenhuma medida, os campos ficam em vermelho ao tentar salvar. -->
          <v-form ref="entryForm" @submit.prevent>
            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-tape-measure</v-icon>
                  <span>Medidas</span>
                  <!-- Nenhum campo sozinho é obrigatório, mas o registro precisa
                       de ao menos uma medida (regra anyMeasure e o backend). -->
                  <span class="ds-form-section__optional">preencha ao menos uma *</span>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.weight_kg"
                    :rules="[anyMeasure]"
                    label="Peso (kg)"
                    type="number"
                    step="0.1"
                    outlined
                    dense
                    hide-details="auto"
                    @input="onWeightOrPercentChange"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.height_cm"
                    :rules="[anyMeasure]"
                    label="Altura (cm)"
                    type="number"
                    step="1"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.waist_cm"
                    :rules="[anyMeasure]"
                    label="Circunferência abdominal (cm)"
                    type="number"
                    step="0.1"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.hip_cm"
                    :rules="[anyMeasure]"
                    label="Quadril (cm)"
                    type="number"
                    step="0.1"
                    outlined
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-col>
              </v-row>
              <!-- Pré-visualização dos calculados (vêm destas medidas) -->
              <v-row dense class="mt-1">
                <v-col cols="12" sm="4">
                  <div class="preview-box">
                    <div class="preview-label">IMC</div>
                    <div class="preview-value">{{ fmt(preview.bmi) }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="preview-box">
                    <div class="preview-label">Cintura/Estatura</div>
                    <div
                      class="preview-value"
                      :style="{ color: colorWaistHeight(preview.waist_height_ratio) }"
                    >
                      {{ fmtRatio(preview.waist_height_ratio) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="preview-box">
                    <div class="preview-label">Cintura/Quadril</div>
                    <div
                      class="preview-value"
                      :style="{ color: colorWaistHip(preview.waist_hip_ratio) }"
                    >
                      {{ fmtRatio(preview.waist_hip_ratio) }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </section>

            <section class="ds-form-section">
              <div class="ds-form-section__head">
                <div class="ds-form-section__title">
                  <v-icon small color="primary" class="mr-2">mdi-human</v-icon>
                  <span>Composição</span>
                </div>
              </div>
              <p class="text-caption text--secondary mb-2">
                <v-icon small>mdi-information-outline</v-icon>
                Informe <strong>% de gordura</strong> ou
                <strong>% de massa magra</strong> — o outro será calculado.
                Com peso informado, os valores em kg também são calculados.
              </p>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.fat_percent"
                    :rules="[anyMeasure]"
                    label="Gordura (%)"
                    type="number"
                    step="0.1"
                    outlined
                    dense
                    hide-details="auto"
                    :background-color="lastEditedPercent === 'fat' ? '#fff8e1' : ''"
                    @input="onFatPercentInput"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.lean_mass_percent"
                    :rules="[anyMeasure]"
                    label="Massa magra (%)"
                    type="number"
                    step="0.1"
                    outlined
                    dense
                    hide-details="auto"
                    :background-color="lastEditedPercent === 'lean' ? '#fff8e1' : ''"
                    @input="onLeanPercentInput"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.fat_kg"
                    label="Gordura (kg) — calculado"
                    type="number"
                    step="0.1"
                    outlined
                    dense
                    hide-details="auto"
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.number="form.lean_mass_kg"
                    label="Massa magra (kg) — calculado"
                    type="number"
                    step="0.1"
                    outlined
                    dense
                    hide-details="auto"
                    readonly
                    filled
                  ></v-text-field>
                </v-col>
              </v-row>
            </section>
          </v-form>
        </v-card-text>

        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="closeForm">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            :loading="saving"
            @click="save"
          >
            <v-icon small left>
              {{ isEditMode ? "mdi-content-save-outline" : "mdi-plus" }}
            </v-icon>
            {{ isEditMode ? "Salvar" : "Adicionar" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { formatDateTime } from "@/utils/datetime";

const emptyForm = () => ({
  weight_kg: null,
  height_cm: null,
  waist_cm: null,
  hip_cm: null,
  fat_percent: null,
  lean_mass_percent: null,
  fat_kg: null,
  lean_mass_kg: null,
});

export default {
  name: "BodyCompositionDialog",

  props: {
    value: { type: Boolean, default: false },
    patientId: { type: [String, Number], required: true },
    gender: { type: String, default: null },
    // Quando true, já abre direto o form (usado pelo AppointmentDialog)
    openDirectAdd: { type: Boolean, default: false },
  },

  setup() {
    const { success, error, warning, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, erroDeRequisicao };
  },

  data() {
    return {
      /** Houve tentativa de salvar sem medida (ver watch measuresSignature). */
      measureErrorsShown: false,
      records: [],
      loading: false,
      formDialogVisible: false,
      isEditMode: false,
      editingId: null,
      form: emptyForm(),
      lastEditedPercent: null, // 'fat' | 'lean' | null
      saving: false,
      deleting: false,
      itemToDelete: null,
    };
  },

  computed: {
    /** Muda quando qualquer uma das 6 medidas muda (ver watch). */
    measuresSignature() {
      const f = this.form || {};
      return [f.weight_kg, f.height_cm, f.waist_cm, f.hip_cm, f.fat_percent, f.lean_mass_percent].join("|");
    },
    internalValue: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },

    isMale() {
      return (this.gender || "").toLowerCase().startsWith("masc");
    },
    waistHipThreshold() {
      return this.isMale ? 0.9 : 0.85;
    },

    preview() {
      // Recalcula derivados em tempo real para exibição
      const w = this.num(this.form.weight_kg);
      const hCm = this.num(this.form.height_cm);
      const waist = this.num(this.form.waist_cm);
      const hip = this.num(this.form.hip_cm);

      const bmi = w && hCm && hCm > 0
        ? +(w / Math.pow(hCm / 100, 2)).toFixed(2)
        : null;

      const wh = waist && hCm && hCm > 0
        ? +(waist / hCm).toFixed(3)
        : null;

      const whp = waist && hip && hip > 0
        ? +(waist / hip).toFixed(3)
        : null;

      return { bmi, waist_height_ratio: wh, waist_hip_ratio: whp };
    },

  },

  watch: {
    /**
     * A regra "ao menos uma medida" é a mesma nos 6 campos: preencher um deles
     * resolve os outros 5, mas o Vuetify só revalida o campo digitado. Depois
     * de uma tentativa com erro, revalida o formulário a cada mudança para os
     * vermelhos sumirem juntos.
     */
    measuresSignature() {
      if (this.measureErrorsShown && this.$refs.entryForm) {
        this.measureErrorsShown = !this.$refs.entryForm.validate();
      }
    },
    value(val) {
      if (val) {
        if (this.openDirectAdd) {
          // Modo entrada rápida: abre direto o form, sem o histórico.
          this.$nextTick(() => this.openAddForm());
        } else {
          this.fetchRecords();
        }
      }
    },
    formDialogVisible(val) {
      // No modo openDirectAdd, fechar o form fecha o dialog inteiro.
      if (!val && this.openDirectAdd) {
        this.$emit("input", false);
      }
    },
  },

  methods: {
    /**
     * Nenhuma medida sozinha é obrigatória, mas o registro precisa de ao menos
     * uma: se todas estiverem vazias, as 6 ficam em vermelho. O backend exige o
     * mesmo (BodyCompositionController).
     */
    anyMeasure() {
      const any = ["weight_kg", "height_cm", "waist_cm", "hip_cm", "fat_percent", "lean_mass_percent"]
        .some((k) => this.num(this.form[k]) !== null);
      return any || "Informe ao menos uma medida";
    },
    formatDate: formatDateTime,
    // ── Data fetching ─────────────────────────────────────────────────
    async fetchRecords() {
      this.loading = true;
      try {
        const { data } = await http.get(
          `/api/body-composition/by-patient/${this.patientId}`,
        );
        if (data?.status === "success") {
          this.records = data.data || [];
        }
      } catch (err) {
        console.error("Erro ao buscar histórico:", err);
        this.erroDeRequisicao(err, "Erro ao carregar histórico");
      } finally {
        this.loading = false;
      }
    },

    closeDialog() {
      this.$emit("input", false);
    },

    // ── Form open/close ───────────────────────────────────────────────
    openAddForm() {
      this.isEditMode = false;
      this.editingId = null;
      this.form = emptyForm();
      this.lastEditedPercent = null;
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.measureErrorsShown = false;
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    openEditForm(item) {
      this.isEditMode = true;
      this.editingId = item.id;
      this.form = {
        weight_kg: this.num(item.weight_kg),
        height_cm: this.num(item.height_cm),
        waist_cm: this.num(item.waist_cm),
        hip_cm: this.num(item.hip_cm),
        fat_percent: this.num(item.fat_percent),
        lean_mass_percent: this.num(item.lean_mass_percent),
        fat_kg: this.num(item.fat_kg),
        lean_mass_kg: this.num(item.lean_mass_kg),
      };
      this.lastEditedPercent = null;
      this.formDialogVisible = true;
      // Abre limpo: sem os vermelhos de uma tentativa anterior.
      this.measureErrorsShown = false;
      this.$nextTick(() => this.$refs.entryForm && this.$refs.entryForm.resetValidation());
    },

    closeForm() {
      this.formDialogVisible = false;
      this.form = emptyForm();
      this.lastEditedPercent = null;
    },

    // ── Reactive calculations ─────────────────────────────────────────
    onFatPercentInput() {
      this.lastEditedPercent = "fat";
      const fat = this.num(this.form.fat_percent);
      if (fat !== null) {
        this.form.lean_mass_percent = +(100 - fat).toFixed(2);
      }
      this.recomputeKg();
    },

    onLeanPercentInput() {
      this.lastEditedPercent = "lean";
      const lean = this.num(this.form.lean_mass_percent);
      if (lean !== null) {
        this.form.fat_percent = +(100 - lean).toFixed(2);
      }
      this.recomputeKg();
    },

    onWeightOrPercentChange() {
      this.recomputeKg();
    },

    recomputeKg() {
      const w = this.num(this.form.weight_kg);
      const fatP = this.num(this.form.fat_percent);
      const leanP = this.num(this.form.lean_mass_percent);

      if (w !== null && fatP !== null) {
        this.form.fat_kg = +((w * fatP) / 100).toFixed(2);
      } else {
        this.form.fat_kg = null;
      }
      if (w !== null && leanP !== null) {
        this.form.lean_mass_kg = +((w * leanP) / 100).toFixed(2);
      } else {
        this.form.lean_mass_kg = null;
      }
    },

    // ── Save / delete ─────────────────────────────────────────────────
    async save() {
      // Sem nenhuma medida, os campos ficam em vermelho; não grava.
      if (!this.$refs.entryForm.validate()) {
        this.measureErrorsShown = true;
        return;
      }
      this.saving = true;
      try {
        const payload = {
          patient_id: this.patientId,
          weight_kg: this.num(this.form.weight_kg),
          height_cm: this.num(this.form.height_cm),
          waist_cm: this.num(this.form.waist_cm),
          hip_cm: this.num(this.form.hip_cm),
          fat_percent: this.num(this.form.fat_percent),
          lean_mass_percent: this.num(this.form.lean_mass_percent),
          fat_kg: this.num(this.form.fat_kg),
          lean_mass_kg: this.num(this.form.lean_mass_kg),
        };

        if (this.isEditMode) {
          await http.put(`/api/body-composition/${this.editingId}`, payload);
          this.success("Medição atualizada!");
        } else {
          await http.post("/api/body-composition/create", payload);
          this.success("Medição adicionada!");
        }

        this.closeForm();
        await this.fetchRecords();
        this.$emit("data-saved", payload);
      } catch (err) {
        console.error("Erro ao salvar:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao salvar medição");
      } finally {
        this.saving = false;
      }
    },

    async confirmDelete(item) {
      this.itemToDelete = item;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Excluir esta medição?",
        hint: "Esta ação não poderá ser desfeita.",
      });
      if (!ok) {
        this.itemToDelete = null;
        return;
      }
      await this.doDelete();
    },

    async doDelete() {
      if (!this.itemToDelete) return;
      this.deleting = true;
      try {
        await http.delete(`/api/body-composition/${this.itemToDelete.id}`);
        this.success("Medição excluída!");
        this.itemToDelete = null;
        await this.fetchRecords();
        this.$emit("data-deleted");
      } catch (err) {
        console.error("Erro ao excluir:", err);
        this.erroDeRequisicao(err, err.response?.data?.message || "Erro ao excluir");
      } finally {
        this.deleting = false;
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────
    num(v) {
      if (v === null || v === undefined || v === "") return null;
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
      return isNaN(n) ? null : n;
    },

    fmt(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = parseFloat(v);
      return isNaN(n) ? "—" : n.toString().replace(".", ",");
    },

    fmtRatio(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = parseFloat(v);
      return isNaN(n) ? "—" : n.toFixed(2).replace(".", ",");
    },

    colorWaistHeight(v) {
      const n = parseFloat(v);
      if (isNaN(n)) return "#9e9e9e";
      return n < 0.5 ? "var(--ds-primary)" : "var(--ds-warning)";
    },

    colorWaistHip(v) {
      const n = parseFloat(v);
      if (isNaN(n)) return "#9e9e9e";
      return n < this.waistHipThreshold ? "var(--ds-primary)" : "var(--ds-warning)";
    },
  },
};
</script>

<style scoped>
/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.ds-dialog-title {
  font-size: 15px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  color: var(--ds-white) !important;
}

.close-btn {
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ Tabela de histórico ═══ */
.th-cell {
  background-color: var(--ds-blue-50) !important;
  border-bottom: 2px solid var(--ds-blue-100) !important;
  padding: 10px !important;
  font-size: 10px !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
}

.history-row {
  transition: background-color 0.15s ease;
}

.history-row:hover {
  background-color: var(--ds-blue-50) !important;
}

.history-row td {
  border-bottom: 1px solid var(--ds-blue-divider) !important;
  font-size: 12px;
  color: var(--ds-text-600);
}

/* Em linha (rótulo à esquerda, valor à direita): metade da altura do bloco
   empilhado, para o formulário caber sem rolar num notebook comum. */
.preview-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--ds-radius);
  background: linear-gradient(135deg, var(--ds-blue-50) 0%, var(--ds-blue-100) 100%);
  border: 1px solid var(--ds-blue-200);
}

.preview-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.preview-value {
  font-size: 16px;
  font-weight: 800;
  color: var(--ds-text-strong);
  letter-spacing: -0.2px;
}
</style>